// React Query mutation hooks for modifying email template data

import { useApiClient } from '@commander/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailTemplateCreateData, EmailTemplateUpdateData } from '../../api/emailTemplateApi';
import { createEmailTemplateApi } from '../../api/emailTemplateApi';
import { communicationKeys } from '../communicationKeys';

/**
 * Hook to render a template server-side (resolves system variables + merges
 * custom variables). Not cached — every apply is a fresh render for the current
 * recipient and variable values.
 */
export function useRenderEmailTemplate() {
  const apiClient = useApiClient();
  const templateApi = createEmailTemplateApi(apiClient);

  return useMutation({
    mutationFn: ({
      templateId,
      tenantContactId,
      variables,
    }: {
      templateId: string;
      tenantContactId?: string | null;
      variables?: Record<string, string>;
    }) => templateApi.renderEmailTemplate(templateId, { tenantContactId, variables }),
  });
}

/**
 * Hook to create a new email template with automatic cache invalidation
 */
export function useCreateEmailTemplate() {
  const apiClient = useApiClient();
  const templateApi = createEmailTemplateApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmailTemplateCreateData) => templateApi.createEmailTemplate(data),
    onSuccess: () => {
      // Invalidate and refetch template lists
      queryClient.invalidateQueries({ queryKey: communicationKeys.templates.email.lists() });
    },
  });
}

/**
 * Hook to update an existing email template with automatic cache invalidation
 */
export function useUpdateEmailTemplate() {
  const apiClient = useApiClient();
  const templateApi = createEmailTemplateApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EmailTemplateUpdateData }) =>
      templateApi.updateEmailTemplate(id, data),
    onSuccess: (updatedTemplate, variables) => {
      // Update the specific template in cache
      queryClient.setQueryData(
        communicationKeys.templates.email.detail(variables.id),
        updatedTemplate
      );
      // Invalidate lists to refetch with updated data
      queryClient.invalidateQueries({ queryKey: communicationKeys.templates.email.lists() });
    },
  });
}

/**
 * Hook to delete an email template with automatic cache invalidation
 */
export function useDeleteEmailTemplate() {
  const apiClient = useApiClient();
  const templateApi = createEmailTemplateApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => templateApi.deleteEmailTemplate(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: communicationKeys.templates.email.detail(deletedId) });
      // Invalidate lists to refetch without deleted template
      queryClient.invalidateQueries({ queryKey: communicationKeys.templates.email.lists() });
    },
  });
}
